import { Services } from "@/services/services.ts";
import { CustomerDocument } from "@/generated/graphql.lazy/customer";
import { browserDetectLanguage } from "@/util/browser-language-detector";
import { Url } from "@/helpers/url";
import { B2cProductPayload, ProductDocument } from "@/generated/graphql.lazy/b2c";
import { Context } from "@/services/context.ts";

export const PARAMETERS_LISTENER_NAME = "parameters";

export type ParametersValue = {
  country: string;
  language: string;
  currency: string;
};

export class Parameters {
  public parameters: ParametersValue = null!;
  public changedParameters: Partial<ParametersValue> = {};
  public detectedParameters: ParametersValue = {} as ParametersValue;
  public dialogVisible = false;
  public key = {
    language: "b2c_user_language",
    country: "b2c_user_country",
    currency: "b2c_user_currency",
  } as const;
  public urlParams: Record<string, string> = {};

  constructor(
    protected context: Context,
    protected services: Services,
  ) {}

  getVisibleLanguage() {
    return this.changedParameters.language ?? this.parameters.language;
  }

  getVisibleCountry() {
    return this.changedParameters?.country ?? this.parameters?.country;
  }

  getVisibleCurrency() {
    return this.changedParameters.currency ?? this.parameters.currency;
  }

  changeParameter<Key extends keyof ParametersValue>(type: Key, value: ParametersValue[Key]) {
    this.changedParameters[type] = value;
  }

  setupDetectedParameters(parameters: { language: string; country: string }) {
    this.detectedParameters = {
      language: parameters.language,
      country: parameters.country,
      currency: null!,
    };
  }

  checkIsDialogProposed() {
    return sessionStorage.getItem("parameters-dialog-proposed") === "true";
  }

  setDialogProposed(proposed: boolean) {
    if (proposed) {
      sessionStorage.setItem("parameters-dialog-proposed", "true");
    } else {
      sessionStorage.removeItem("parameters-dialog-proposed");
    }
  }

  setDialogVisible(visible: boolean) {
    this.dialogVisible = visible;
    this.context.notify(PARAMETERS_LISTENER_NAME);
  }

  applyDialogResult(result: { type: "rejected" } | { type: "confirmed"; language: string; country: string }) {
    this.setDialogProposed(true);
    this.setDialogVisible(false);

    if (result.type === "confirmed") {
      this.changeParameter("language", result.language);
      this.changeParameter("country", result.country);

      this.applyParameters();
    }

    this.context.notify(PARAMETERS_LISTENER_NAME);
  }

  async applyParameters() {
    await this.updateParametersFromUserInput({
      ...this.parameters,
      ...this.changedParameters,
    });
    this.changedParameters = {};
    this.context.notify(PARAMETERS_LISTENER_NAME);
    this.services.initialize.reinitialize();
  }

  normalizeParameters(value: Partial<ParametersValue>) {
    const parameters = { ...value };

    // case normalization
    // uppsercased: country, currency
    // lowercased: language
    // reason: server side values format

    if (parameters.language) {
      parameters.language = parameters.language?.toLocaleLowerCase();
    }

    if (parameters.country) {
      parameters.country = parameters.country?.toLocaleUpperCase();
    }

    if (parameters.currency) {
      parameters.currency = parameters.currency?.toUpperCase();
    }

    return parameters;
  }

  isParametersNormalizationNeeded(parameters: Partial<ParametersValue>) {
    return !this.equalsParameters(this.parameters, parameters, ["country", "language"]);
  }

  equalsParameters(a: Partial<ParametersValue>, b: Partial<ParametersValue>, keys = ["country", "language", "currency"]) {
    //@ts-expect-error
    return keys.every((key) => a[key]?.toLocaleLowerCase() === b[key]?.toLocaleLowerCase());
  }

  async readParametersFromLocalStorage() {
    try {
      const parameters = localStorage.getItem("parameters");
      if (parameters) {
        return JSON.parse(parameters) as ParametersValue;
      }
    } catch (error) {
      console.error("Error reading parameters from local storage", error);
    }
  }

  async writeParametersToApiPreferences(parameters: ParametersValue) {
    const countryId = this.services.countries.getChannelCountries().find((item) => item.code === parameters.country)?.id;
    const customerResponse = await this.services.api.client.mutate({
      mutation: CustomerDocument,
      variables: {
        input: {
          countryId: countryId,
          currency: parameters.currency,
          locale: parameters.language,
        },
      },
    });
  }

  async updateParametersFromUserInput(parameters: ParametersValue) {
    await this.updateParameters(parameters, "api");
  }

  async updateParameters(parameters: ParametersValue, type: "local" | "api") {
    if (!this.equalsParameters(this.parameters, parameters)) {
      this.parameters = parameters;

      if (type === "api" && this.services.customerChannel.customer) {
        await this.writeParametersToApiPreferences(parameters);
      }

      if (type === "api" || type === "local") {
        this.setSavedParameters(parameters);
      }
    }
  }

  async fillLocalParametersByCustomerPreferences() {
    const customerParameters = this.getCustomerParameters();

    this.parameters = {
      country: customerParameters.country ?? this.parameters.country,
      language: customerParameters.language ?? this.parameters.language,
      currency: customerParameters.currency ?? this.parameters.currency,
    };
  }

  prepareForUrl(values: Partial<ParametersValue>) {
    return {
      lang: values.language?.toLowerCase()!,
    };
  }

  getVisitorParameters() {
    return {
      language: browserDetectLanguage(),
      country: null,
    };
  }

  getUrlParameters() {
    return {
      language: this.urlParams.lang ?? null,
    };
  }

  getCustomerParameters() {
    const customer = this.services.customerChannel.customer;

    return {
      language: customer?.locale ?? null,
      country: customer?.country?.code ?? null,
      currency: customer?.currency ?? null,
    };
  }

  getSavedParameters() {
    return {
      language: this.services.cookies.get(this.key.language) ?? null,
      country: this.services.cookies.get(this.key.country) ?? null,
      currency: this.services.cookies.get(this.key.currency) ?? null,
    };
  }

  setSavedParameters(preferences: Partial<ParametersValue>) {
    this.services.cookies.set(this.key.language, preferences.language ?? null);
    this.services.cookies.set(this.key.country, preferences.country ?? null);
    this.services.cookies.set(this.key.currency, preferences.currency ?? null);
  }

  setupAppliedParameters(parameters: { language: string; country: string; currency: string }) {
    this.parameters = this.normalizeParameters(parameters) as ParametersValue;
  }

  activateDialogIfNeed() {
    if (this.checkIsDialogNeeded()) {
      this.setDialogVisible(true);
    }
  }

  checkIsDialogNeeded() {
    if (this.checkIsDialogProposed()) {
      return false;
    }

    return this.isNotEnoughParameters();
  }

  isParametersFullyDefined(parameters: { language?: string | null; country?: string | null }) {
    return Boolean(parameters.language && parameters.country);
  }

  isNotEnoughParameters() {
    const urlParameters = this.getUrlParameters();
    const customerParameters = this.getCustomerParameters();
    const savedParameters = this.getSavedParameters();

    const merged = {
      language: urlParameters.language ?? customerParameters.language ?? savedParameters.language,
      country: customerParameters.country ?? savedParameters.country,
    };

    return !this.isParametersFullyDefined(merged);
  }

  async migrateParameters() {
    // remove after some time
    try {
      await this.tryMigrateParameters();
    } catch (error) {
      console.error("Error migrating parameters", error);
    }
  }

  async tryMigrateParameters() {
    const parametersMigrated = localStorage.getItem("parameters-migrated");
    if (parametersMigrated) {
      return;
    }

    const parameters = await this.readParametersFromLocalStorage();

    if (!parameters) {
      return;
    }

    if (parameters?.language) {
      this.services.cookies.set(this.key.language, parameters.language);
    }

    if (parameters?.country) {
      this.services.cookies.set(this.key.country, parameters.country);
    }

    if (parameters?.currency) {
      this.services.cookies.set(this.key.currency, parameters.currency);
    }

    localStorage.setItem("parameters-migrated", "true");
  }
}
